// Shared building blocks of the application review surfaces.
//
// Extracted from the applications list so the full-page review workspace and
// the list can render the same fields, the same document rows and the same
// edit/review state without either becoming the other's dumping ground.

import {
  Alert,
  Box,
  Button,
  Chip,
  InputBase,
  Stack,
  Typography,
} from '@mui/material';
import { createContext, useContext, useEffect, useState } from 'react';

import { onboardingApi, type DocumentReview } from '../../../api/onboarding.api.js';
import { APP_LOCALE } from '../../../utils/locale.js';

export function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

export function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

export function asArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map((item) => asRecord(item)) : [];
}

export interface FieldProps {
  label: string;
  value: string;
  /** Presentation only; editing continues to use the stored value. */
  displayValue?: string;
  /** Dot path into the snapshot; presence makes the row editable in edit mode. */
  path?: string;
}

/**
 * Drawer-wide edit state. Context rather than props because the snapshot view
 * is a deep tree of Field rows and the edit facility must not require
 * threading callbacks through every block.
 */
export interface PendingEdit {
  readonly label: string;
  readonly oldValue: string;
  readonly value: string;
}
export interface EditState {
  readonly editing: boolean;
  readonly pending: ReadonlyMap<string, PendingEdit>;
  readonly stage: (path: string, label: string, oldValue: string, value: string) => void;
  /**
   * Decided applications: only blanks may be filled.
   *
   * The answers a reviewer decided on are evidence and stay as they are; a
   * field nobody was ever asked for can still be completed. Enforced on the
   * server too — this only keeps the console from offering a box that would
   * be refused on save.
   */
  readonly completeOnly?: boolean;
}
export const EditContext = createContext<EditState>({
  editing: false,
  pending: new Map(),
  stage: () => undefined,
});

/**
 * Document review state, by document id — same reasoning as EditContext: the
 * document rows sit at the bottom of a deep tree.
 *
 * The status shown here comes from the vault, NOT from the application
 * snapshot: the snapshot's status is whatever the customer's own flow wrote,
 * which is why every file read "PENDING" forever and no reviewer decision had
 * anywhere to live.
 */
export interface ReviewState {
  readonly reviews: ReadonlyMap<string, DocumentReview>;
  readonly canDecide: boolean;
  readonly decide: (docId: string, decision: 'VERIFY' | 'REJECT', note?: string) => Promise<void>;
}
export const ReviewContext = createContext<ReviewState>({
  reviews: new Map(),
  canDecide: false,
  decide: () => Promise.resolve(),
});

/**
 * One label/value row.
 *
 * A two-column grid with the value LEFT-aligned right after its label, not
 * pushed to the far edge of the page: on a wide screen the old layout left a
 * metre of empty space between a label and its value, so following a row meant
 * tracking across the whole viewport, and adjacent rows blurred together. The
 * grid keeps the pair adjacent whatever the window width, and a hairline
 * separator gives the eye a rail to follow.
 */
export function Field({ label, value, displayValue, path }: FieldProps): JSX.Element {
  const edit = useContext(EditContext);
  const rowSx = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '220px minmax(0, 1fr)' },
    columnGap: 3,
    alignItems: 'baseline',
    py: 0.75,
    borderBottom: '1px solid',
    borderColor: 'rgba(255, 255, 255, 0.05)',
  } as const;

  // Editable only where a snapshot path is known; composed display rows
  // (joined addresses, name pairs) stay read-only rather than pretending.
  // On a decided application only the blanks open, because everything else is
  // what the decision was made on.
  const lockedByDecision = edit.completeOnly === true && value.trim() !== '';
  if (edit.editing && path !== undefined && !lockedByDecision) {
    const staged = edit.pending.get(path);
    return (
      <Box sx={rowSx}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <InputBase
          value={staged?.value ?? value}
          onChange={(event) => edit.stage(path, label, value, event.target.value)}
          sx={{
            fontSize: 14,
            px: 1,
            py: 0.25,
            border: '1px solid',
            borderColor: staged !== undefined ? 'warning.main' : 'divider',
            borderRadius: 1,
            '& input': { p: 0 },
          }}
        />
      </Box>
    );
  }
  return (
    <Box sx={rowSx}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
        {value === '' ? '—' : displayValue ?? value}
      </Typography>
    </Box>
  );
}

export interface DocRef {
  /** Server document id. Empty for legacy refs (name only, no stored bytes). */
  id: string;
  label: string;
  name: string;
  sizeLabel: string;
  status: string;
}

/**
 * Normalises a snapshot document field into a DocRef. Handles both the current
 * object shape ({ id, name, sizeLabel, status }) and the legacy bare-filename
 * string captured before the document vault existed.
 */
export function asDocRef(value: unknown): Omit<DocRef, 'label'> | null {
  if (typeof value === 'object' && value !== null) {
    const rec = value as Record<string, unknown>;
    const name = asString(rec['name']);
    if (name.length === 0) return null;
    return {
      id: asString(rec['id']),
      name,
      sizeLabel: asString(rec['sizeLabel']),
      status: asString(rec['status']) || 'PENDING',
    };
  }
  if (typeof value === 'string' && value.length > 0) {
    return { id: '', name: value, sizeLabel: '', status: 'PENDING' };
  }
  return null;
}

/** String arrays survive as strings. asArray() coerces elements to records, so
 *  a string[] rendered through it came out as "[object Object]". */
export function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/** One address on one line, in reading order. */
export function joinAddress(address: Record<string, unknown>): string {
  return [
    asString(address['streetAddress']),
    asString(address['city']),
    asString(address['region']),
    asString(address['postalCode']),
    asString(address['country']),
  ]
    .filter((part) => part !== '')
    .join(', ');
}

/** "proof-registered-address" -> "Proof of registered address". */
export function humanRequirement(id: string): string {
  const words = id.replace(/[-_]+/g, ' ').trim();
  return words.length === 0 ? 'Document' : words.charAt(0).toUpperCase() + words.slice(1);
}

/** Collects every document the applicant attached, from across the snapshot. */
export function collectDocuments(snapshot: Record<string, unknown>): DocRef[] {
  const docs: DocRef[] = [];
  const identity = asRecord(snapshot['identity']);
  const identityDoc = asDocRef(identity['uploadedDocument']);
  if (identityDoc !== null) {
    // A real ID document was uploaded to the vault — previewable.
    docs.push({
      ...identityDoc,
      label: 'Identity document',
      status: identity['verified'] === true ? 'VERIFIED' : identityDoc.status,
    });
  }
  // No synthetic row when nothing was uploaded here.
  //
  // This used to invent a document from the identity METHOD — a "NATIONAL_ID"
  // entry with no bytes, which a reviewer could neither open nor decide, and
  // which claimed a document existed when none did. The provider performs the
  // capture now: the real ID document, the selfie and the address proof live at
  // the provider and reach this tab through the vault import on the Identity
  // tab. Listing a placeholder beside them describes a file nobody has.

  const business = asRecord(snapshot['business']);

  // Section E's checklist is where every business file now lives, keyed by the
  // requirement it answers. The reviewer used to be shown documents gathered
  // from four per-screen buckets; those buckets were removed when uploads moved
  // to one named checklist, and reading them alone means every business
  // application shows no documents at all.
  const checklist = asRecord(business['checklist']);
  for (const [requirementId, value] of Object.entries(checklist)) {
    for (const doc of asArray(value)) {
      const ref = asDocRef(doc);
      // The stored filename already carries the document's name, so the label
      // only has to identify the checklist row it answers.
      if (ref !== null) docs.push({ ...ref, label: humanRequirement(requirementId) });
    }
  }

  // Applications submitted before the checklist existed still carry the old
  // per-screen fields; keep reading them so historic cases stay reviewable.
  const company = asRecord(business['company']);
  for (const doc of asArray(company['documents'])) {
    const ref = asDocRef(doc);
    if (ref !== null) docs.push({ ...ref, label: 'Corporate document (legacy)' });
  }
  const trading = asRecord(business['tradingAddress']);
  const tradingRef = asDocRef(trading['proofDocument']);
  if (tradingRef !== null) docs.push({ ...tradingRef, label: 'Trading-address proof (legacy)' });
  for (const director of asArray(business['directors'])) {
    const ref = asDocRef(asRecord(director)['proofAddress']);
    if (ref !== null) {
      const who = `${asString(asRecord(director)['firstName'])} ${asString(asRecord(director)['lastName'])}`.trim();
      docs.push({ ...ref, label: `Director proof of address${who ? ` — ${who}` : ''} (legacy)` });
    }
  }
  for (const ubo of asArray(business['ubos'])) {
    const who = `${asString(asRecord(ubo)['firstName'])} ${asString(asRecord(ubo)['lastName'])}`.trim();
    for (const doc of asArray(asRecord(ubo)['documents'])) {
      const ref = asDocRef(doc);
      if (ref !== null) docs.push({ ...ref, label: `UBO document${who ? ` — ${who}` : ''} (legacy)` });
    }
  }
  return docs;
}

/**
 * One document row in the review drawer: name + status, with inline preview and
 * download. The bytes are fetched with the admin's Bearer token and turned into
 * an object URL — inline <img>/<iframe> elements can't send auth headers, so
 * the protected fetch happens up front and the ephemeral blob URL is local.
 */
export function DocumentItem({ applicationId, doc }: { applicationId: string; doc: DocRef }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [decodeFailed, setDecodeFailed] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [type, setType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deciding, setDeciding] = useState(false);

  const previewable = doc.id !== '';
  const review = useContext(ReviewContext);
  const stored = doc.id === '' ? undefined : review.reviews.get(doc.id);
  // The vault's disposition wins; a legacy ref with no stored bytes keeps the
  // snapshot's own status because there is no row to review.
  const status = stored?.status ?? doc.status;

  const decide = async (decision: 'VERIFY' | 'REJECT'): Promise<void> => {
    let note: string | undefined;
    if (decision === 'REJECT') {
      const entered = window.prompt(`Why is "${doc.name}" rejected? (recorded in the audit log)`);
      if (entered === null) return;
      if (entered.trim().length < 4) {
        setError('A rejection needs a reason of at least 4 characters.');
        return;
      }
      note = entered.trim();
    }
    setDeciding(true);
    setError(null);
    try {
      await review.decide(doc.id, decision, note);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the decision');
    } finally {
      setDeciding(false);
    }
  };

  // Revoke the object URL when it changes or the row unmounts.
  useEffect((): (() => void) => {
    return () => {
      if (url !== null) URL.revokeObjectURL(url);
    };
  }, [url]);

  const ensureLoaded = async (): Promise<string | null> => {
    if (url !== null) return url;
    setLoading(true);
    setError(null);
    try {
      const blob = await onboardingApi.fetchDocumentBlob(applicationId, doc.id);
      const objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
      setType(blob.type);
      return objectUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load document');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const togglePreview = async (): Promise<void> => {
    if (open) {
      setOpen(false);
      return;
    }
    const loaded = await ensureLoaded();
    if (loaded !== null) setOpen(true);
  };

  const download = async (): Promise<void> => {
    const loaded = await ensureLoaded();
    if (loaded === null) return;
    const anchor = document.createElement('a');
    anchor.href = loaded;
    anchor.download = doc.name;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  return (
    <Box sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap title={doc.name}>
            {doc.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {doc.label}
            {doc.sizeLabel !== '' ? ` · ${doc.sizeLabel}` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <Chip
            label={status}
            size="small"
            color={status === 'VERIFIED' ? 'success' : status === 'REJECTED' ? 'error' : 'default'}
          />
          {previewable ? (
            <>
              <Button size="small" onClick={() => void togglePreview()} disabled={loading}>
                {loading && !open ? '…' : open ? 'Hide' : 'Preview'}
              </Button>
              <Button size="small" onClick={() => void download()} disabled={loading}>
                Download
              </Button>
            </>
          ) : null}
          {/* A document nobody has dispositioned blocks the approval, so the
              decision belongs on the row the reviewer is looking at. */}
          {previewable && review.canDecide && stored !== undefined ? (
            <>
              <Button
                size="small"
                color="success"
                disabled={deciding || status === 'VERIFIED'}
                onClick={() => void decide('VERIFY')}
              >
                {deciding ? '…' : 'Approve'}
              </Button>
              <Button
                size="small"
                color="error"
                disabled={deciding}
                onClick={() => void decide('REJECT')}
              >
                Reject
              </Button>
            </>
          ) : null}
        </Stack>
      </Box>

      {stored?.reviewedAt !== null && stored?.reviewedAt !== undefined ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {stored.status === 'VERIFIED' ? 'Approved' : 'Rejected'} by {stored.reviewedBy ?? '—'} ·{' '}
          {new Date(stored.reviewedAt).toLocaleString(APP_LOCALE)}
          {stored.reviewNote !== null && stored.reviewNote !== '' ? ` · ${stored.reviewNote}` : ''}
        </Typography>
      ) : null}

      {error !== null ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      ) : null}

      {open && url !== null ? (
        <Box
          sx={{
            mt: 1,
            height: 420,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: 'background.default',
          }}
        >
          {decodeFailed ? (
            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: 2 }}>
              <Typography variant="body2" color="text.secondary" align="center">
                The file could not be rendered as an image — its contents do not match its
                type. Download it to inspect the actual bytes.
              </Typography>
            </Stack>
          ) : type.startsWith('image/') ? (
            <Box
              component="img"
              src={url}
              alt={doc.name}
              onError={() => setDecodeFailed(true)}
              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <Box
              component="iframe"
              src={url}
              title={doc.name}
              sx={{ width: '100%', height: '100%', border: 0 }}
            />
          )}
        </Box>
      ) : null}

      {!previewable ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Captured before the document vault — ask the applicant to re-upload to preview.
        </Typography>
      ) : null}
    </Box>
  );
}
